import { useEffect, useState } from 'react'
import { useSiteContentState } from './useSiteContent'

// Copie de secours embarquée à la compilation : le site (CRA, JavaScript) ne
// peut pas importer le paquet de contrats TypeScript, donc ces valeurs sont
// dupliquées ici depuis DEFAULT_BOOKING_SETTINGS
// (packages/contracts/src/bookingSettings.ts). Un seul exemplaire pour tout
// le site : les pages Planity (ReserverEnLigne, PlanityGift) passent toutes
// par ce hook plutôt que de recopier l'objet chacune de son côté. Cette copie ne sert plus qu'à afficher l'adresse
// publique Planity le temps que la vraie valeur arrive ou en cas de panne
// (cf. `status` ci-dessous) — elle n'est plus jamais utilisée comme clé du
// module, voir Finding 2.
const BOOKING_FALLBACK = {
  planityWidgetKey: '-MmNxEmEfwgR5sqRSHw2',
  planityPublicUrl: 'https://www.planity.com/azoya-spa-89290-auxerre',
}

// Un vrai coupe-circuit, pas un pari sur la vitesse du réseau. fetchPublic()
// n'a pas de délai interne : une connexion authentiquement bloquée resterait
// « en attente » indéfiniment sans ce filet, ce qui priverait la page de
// tout module de réservation ET de tout message d'explication. La valeur
// est délibérément large — largement au-delà d'un aller-retour réseau
// normal, même lent — car elle ne doit se déclencher que pour une requête
// qui ne se termine vraiment jamais, jamais pour arbitrer une simple
// lenteur.
//
// Une version antérieure de ce hook utilisait un délai d'une seconde pour
// choisir entre « copie de secours » et « vraie valeur » dès que la
// requête n'avait pas encore abouti : c'était une course contre le réseau,
// pas un coupe-circuit, et une réponse arrivée à 1,1 seconde perdait la
// course pour rien — le widget restait injecté avec la copie de secours
// pour le reste de la page, sans aucun signal nulle part (voir le rapport
// de tâche 14, Finding 1). Le délai ci-dessous ne tranche plus jamais entre
// deux valeurs : il ne fait que déclarer forfait sur une requête qui n'a
// donné aucun signe de vie, succès ou échec, après un délai que plus aucune
// réponse légitime ne devrait dépasser.
const DELAI_COUPE_CIRCUIT_MS = 8000

export function useBookingSettings() {
  const { data: booking, status: fetchStatus } = useSiteContentState('booking-settings', BOOKING_FALLBACK)
  const [hungConnection, setHungConnection] = useState(false)

  useEffect(() => {
    if (fetchStatus !== 'pending') return
    const timeout = setTimeout(() => setHungConnection(true), DELAI_COUPE_CIRCUIT_MS)
    return () => clearTimeout(timeout)
  }, [fetchStatus])

  // Trois états, pas un booléen « prêt » :
  // - 'loading'     la requête est en cours ; on attend, on n'injecte rien.
  // - 'ready'       la requête a réussi ; booking.planityWidgetKey est la
  //                 vraie valeur reçue de l'API, sûre à injecter.
  // - 'unavailable' la requête a échoué, ou n'a authentiquement jamais
  //                 abouti (coupe-circuit). Les trois pages Planity ne
  //                 doivent JAMAIS injecter le module dans cet état : une
  //                 clé de secours qui vient peut-être d'être révoquée côté
  //                 Planity (c'est précisément pour la changer que cet
  //                 écran d'administration existe) est pire qu'une absence
  //                 de module — un visiteur qui tombe sur un module qui ne
  //                 s'authentifie pas est un visiteur perdu, un visiteur
  //                 qui voit qu'il doit téléphoner est une réservation
  //                 sauvée. Seule booking.planityPublicUrl reste utilisable
  //                 ici : une simple adresse publique, pas un identifiant,
  //                 qui change rarement et donne une autre voie de
  //                 réservation.
  //
  // Le passage à 'ready' l'emporte toujours sur hungConnection : si la
  // requête finit par aboutir après le coupe-circuit, on préfère basculer
  // sur la vraie valeur plutôt que de rester bloqué sur « indisponible » —
  // le module s'affiche alors avec un peu de retard plutôt que jamais.
  const status =
    fetchStatus === 'success' ? 'ready' : fetchStatus === 'error' || hungConnection ? 'unavailable' : 'loading'

  return { booking, status }
}
